import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Search, FileText, TrendingUp, Zap, X, Factory as FactoryIcon,
  Users, Package, ChevronDown, ChevronUp, AlertTriangle, Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import RoleBasedHeader from "../../components/ui/RoleBasedHeader";
import StatTile from "../my-submissions/components/StatTile";
import EmailsTab from "./components/EmailsTab";

// STATUS_TO_TAB kept aligned with My Submissions for the same vocabulary, but this page
// deliberately shows EVERY row regardless of quantity or status — this is the permanent
// audit trail, not the "what needs my attention right now" operational view. "expired" is
// the one exception: it's a dedicated, actionable tab (mirrors My Submissions' Expired tab)
// since it's the one state that genuinely needs a human decision.
const STATUS_LABEL = { pending_approval: "Pending", rejected: "Rejected", live: "Live", expired: "Expired", withdrawn: "Withdrawn" };
const STATUS_BADGE = {
  live: { bg: "#dcfce7", color: "#166534" },
  pending_approval: { bg: "#fef3c7", color: "#92400e" },
  rejected: { bg: "#fee2e2", color: "#b91c1c" },
  expired: { bg: "#ffedd5", color: "#9a3412" },
  withdrawn: { bg: "#f3f4f6", color: "#4b5563" },
};

// Only factory_relations_officer and admin have access to Submit Rates (Factory Rate
// Intelligence Center) — quota_manager does not, so the "Update & Resubmit" deep-link
// is hidden for that role rather than sending it to a page it can't open.
const CAN_RESUBMIT_ROLES = ["factory_relations_officer", "admin"];

const ADMIN_ROLES = ["admin", "quota_manager"];

// Two-step fetch (mill_price_quotes, then mi_mills by distinct id) — same pattern as
// /mandi's fetchMandiListings (src/pages/mandi/data.js) rather than a Supabase embed,
// since mi_mills relations aren't consistently embeddable across this codebase's queries.
const fetchSubmissionHistory = async (isAdminView) => {
  const { data: rows, error: rowsErr } = await supabase
    ?.from("mill_price_quotes")
    ?.select("*")
    ?.order("submitted_at", { ascending: false });

  if (rowsErr) return { rows: [], officerMap: {}, error: rowsErr?.message };
  if (!rows || rows?.length === 0) return { rows: [], officerMap: {}, error: null };

  const millIds = [...new Set(rows?.map((r) => r?.mill_id)?.filter(Boolean))];
  const { data: mills, error: millsErr } = await supabase
    ?.from("mi_mills")
    ?.select("id, canonical_name, source_state, source_district")
    ?.in("id", millIds);
  if (millsErr) return { rows: [], officerMap: {}, error: `mi_mills: ${millsErr?.message}` };

  const millById = {};
  mills?.forEach((m) => { millById[m?.id] = m; });

  // Officer name resolution — only meaningful (and only permitted by RLS) for admin/quota_manager,
  // who can read every profile. An FRO's own rows all carry their own submitted_by, which they
  // can already resolve via the "read own profile" RLS policy, but there's nothing to look up:
  // it's always just them, so we skip the extra round trip entirely for that view.
  let officerMap = {};
  if (isAdminView) {
    const officerIds = [...new Set([
      ...rows?.map((r) => r?.submitted_by),
      ...rows?.map((r) => r?.withdrawn_by),
    ]?.filter(Boolean))];
    const { data: officers, error: officersErr } = await supabase
      ?.from("user_profiles")
      ?.select("user_id, full_name, email")
      ?.in("user_id", officerIds);
    if (officersErr) return { rows: [], officerMap: {}, error: `user_profiles: ${officersErr?.message}` };
    officers?.forEach((o) => { officerMap[o?.user_id] = o; });
  }

  // Same rule as My Submissions: expired = still live, still has stock, lifting date
  // passed, and no NEWER (live or pending_approval) submission for the same mill+grade
  // already superseded it. A rejected resubmission does not clear this flag.
  const todayStr = new Date()?.toISOString()?.slice(0, 10);
  // Treat a missing submitted_at as "very old" rather than letting the raw comparison
  // silently no-op both directions (JS coerces a null/string comparison to NaN, which
  // is never > or < anything) — found during a recheck pass, hardened defensively.
  const safeSubmittedAt = (row) => row?.submitted_at || '0001-01-01';
  const isSuperseded = (row) =>
    rows?.some((other) =>
      other?.id !== row?.id &&
      other?.mill_id === row?.mill_id &&
      other?.grade === row?.grade &&
      safeSubmittedAt(other) > safeSubmittedAt(row) &&
      (other?.status === "live" || other?.status === "pending_approval")
    );

  const mapped = rows?.map((row) => {
    const isExpired =
      row?.status === "live" &&
      Number(row?.available_qty) > 0 &&
      row?.lifting_date &&
      row?.lifting_date < todayStr &&
      !isSuperseded(row);
    return {
    id: row?.id,
    millId: row?.mill_id,
    millName: millById?.[row?.mill_id]?.canonical_name || "Unknown mill",
    sourceState: millById?.[row?.mill_id]?.source_state,
    sourceDistrict: millById?.[row?.mill_id]?.source_district,
    grade: row?.grade,
    season: row?.season,
    rateExcl: row?.rate_excl_gst,
    qty: Number(row?.available_qty) || 0,
    paymentDate: row?.payment_date,
    liftingDate: row?.lifting_date,
    status: row?.status,
    isExpired,
    submittedBy: row?.submitted_by,
    submittedByName: officerMap?.[row?.submitted_by]?.full_name || officerMap?.[row?.submitted_by]?.email || null,
    submittedAt: row?.submitted_at,
    approvedByRole: row?.approved_by_role,
    approvedAt: row?.approved_at,
    rejectedAt: row?.rejected_at,
    rejectionReason: row?.rejection_reason,
    withdrawnAt: row?.withdrawn_at,
    withdrawnByName: officerMap?.[row?.withdrawn_by]?.full_name || officerMap?.[row?.withdrawn_by]?.email || null,
  };
  });

  return { rows: mapped, officerMap, error: null };
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso)?.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE?.[status] || STATUS_BADGE?.pending_approval;
  return (
    <span
      className="px-2.5 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap"
      style={{ background: s?.bg, color: s?.color }}
    >
      {STATUS_LABEL?.[status] || status}
    </span>
  );
};

const SubmissionHistory = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const canResubmit = CAN_RESUBMIT_ROLES?.includes(userProfile?.role);
  const isAdminView = ADMIN_ROLES?.includes(userProfile?.role);

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [rows, setRows] = useState([]);
  const [activeView, setActiveView] = useState("factory"); // 'factory' | 'officer' | 'log'
  const [searchQuery, setSearchQuery] = useState("");
  const [officerFilter, setOfficerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedFactory, setExpandedFactory] = useState(null);
  const [expandedOfficer, setExpandedOfficer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      const { rows: r, error } = await fetchSubmissionHistory(isAdminView);
      if (cancelled) return;
      if (error) { setFetchError(error); setRows([]); }
      else { setRows(r); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminView]);

  // ── By-Factory aggregation ──────────────────────────────────────────────
  const byFactory = useMemo(() => {
    const map = {};
    rows?.forEach((r) => {
      const key = r?.millId;
      if (!map?.[key]) {
        map[key] = {
          millId: key,
          millName: r?.millName,
          totalSubmissions: 0,
          liveCount: 0,
          liveQty: 0,
          pendingCount: 0,
          rejectedCount: 0,
          lastSubmittedAt: r?.submittedAt,
          lastSubmittedByName: r?.submittedByName,
          rows: [],
        };
      }
      const f = map[key];
      f.totalSubmissions += 1;
      if (r?.status === "live") { f.liveCount += 1; f.liveQty += r?.qty; }
      if (r?.status === "pending_approval") f.pendingCount += 1;
      if (r?.status === "rejected") f.rejectedCount += 1;
      if (r?.submittedAt > f?.lastSubmittedAt) {
        f.lastSubmittedAt = r?.submittedAt;
        f.lastSubmittedByName = r?.submittedByName;
      }
      f.rows?.push(r);
    });
    return Object.values(map)?.sort((a, b) => (a?.millName || "")?.localeCompare(b?.millName || ""));
  }, [rows]);

  // ── By-Officer aggregation (admin/quota_manager only) ───────────────────
  const byOfficer = useMemo(() => {
    if (!isAdminView) return [];
    const map = {};
    rows?.forEach((r) => {
      const key = r?.submittedBy || "unknown";
      if (!map?.[key]) {
        map[key] = {
          submittedBy: key,
          name: r?.submittedByName || "Unknown officer",
          totalSubmissions: 0,
          liveCount: 0,
          liveQty: 0,
          pendingCount: 0,
          rejectedCount: 0,
          factoryIds: new Set(),
          rows: [],
        };
      }
      const o = map[key];
      o.totalSubmissions += 1;
      if (r?.status === "live") { o.liveCount += 1; o.liveQty += r?.qty; }
      if (r?.status === "pending_approval") o.pendingCount += 1;
      if (r?.status === "rejected") o.rejectedCount += 1;
      o.factoryIds?.add(r?.millId);
      o.rows?.push(r);
    });
    return Object.values(map)
      ?.map((o) => ({
        ...o,
        factoriesCount: o?.factoryIds?.size,
        approvalRate: (o?.liveCount + o?.rejectedCount) > 0
          ? Math.round((o?.liveCount / (o?.liveCount + o?.rejectedCount)) * 100)
          : 100,
      }))
      ?.sort((a, b) => b?.totalSubmissions - a?.totalSubmissions);
  }, [rows, isAdminView]);

  // ── Officer dropdown options (admin/quota_manager only) ─────────────────
  const officerOptions = useMemo(
    () => byOfficer?.map((o) => ({ id: o?.submittedBy, name: o?.name })),
    [byOfficer]
  );

  // ── Raw log filtering ────────────────────────────────────────────────────
  const filteredLog = useMemo(() => {
    let result = rows;
    if (searchQuery?.trim()) {
      const q = searchQuery?.trim()?.toLowerCase();
      result = result?.filter((r) => r?.millName?.toLowerCase()?.includes(q));
    }
    if (isAdminView && officerFilter !== "all") {
      result = result?.filter((r) => r?.submittedBy === officerFilter);
    }
    if (statusFilter !== "all") {
      result = result?.filter((r) => r?.status === statusFilter);
    }
    return result;
  }, [rows, searchQuery, officerFilter, statusFilter, isAdminView]);

  // ── Summary stats (unfiltered) ──────────────────────────────────────────
  const totalSubmissions = rows?.length;
  const totalFactories = byFactory?.length;
  const totalLiveQty = rows?.filter((r) => r?.status === "live")?.reduce((s, r) => s + r?.qty, 0);
  const expiredRows = useMemo(
    () => rows?.filter((r) => r?.isExpired)?.sort((a, b) => (a?.liftingDate < b?.liftingDate ? -1 : 1)),
    [rows]
  );

  const TABS = [
    { key: "factory", label: "By Factory", icon: FactoryIcon },
    ...(isAdminView ? [{ key: "officer", label: "By Officer", icon: Users }] : []),
    { key: "expired", label: `Expired${expiredRows?.length ? ` (${expiredRows?.length})` : ""}`, icon: AlertTriangle },
    { key: "log", label: "All Submissions", icon: FileText },
    { key: "emails", label: "Emails", icon: Mail },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#faf7f2", fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>Submission History – SugarHathi</title>
        <meta name="description" content="Permanent audit trail of every mill rate submission — by factory and by officer." />
      </Helmet>
      <RoleBasedHeader />
      <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-extrabold mb-1" style={{ color: "#16354f" }}>
            Submission History
          </h1>
          <p className="text-sm mb-4" style={{ color: "#52606d" }}>
            {isAdminView
              ? "Every rate submission across every officer — including exhausted and rejected rows. Nothing is ever hidden here."
              : "Your complete submission record — including exhausted and rejected rows. Nothing is ever hidden here."}
          </p>
        </div>

        {/* ── Summary Strip ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <StatTile
            icon={FileText}
            label="Total Submissions"
            value={totalSubmissions}
            style={{ background: "linear-gradient(135deg, #1e4a72, #16354f)" }}
          />
          <StatTile
            icon={FactoryIcon}
            label="Factories Covered"
            value={totalFactories}
            style={{ background: "linear-gradient(135deg, #d97706, #92400e)" }}
          />
          <StatTile
            icon={Zap}
            label="Currently Live Qty (MT)"
            value={totalLiveQty}
            style={{ background: "linear-gradient(135deg, #0f766e, #0b5c54)" }}
          />
        </div>

        {/* ── View tabs ── */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {TABS?.map(({ key, label, icon: TabIcon }) => {
            const isActive = activeView === key;
            return (
              <button
                key={key}
                onClick={() => setActiveView(key)}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors duration-150"
                style={
                  isActive
                    ? { background: "#16354f", color: "#fff", border: "1px solid #16354f" }
                    : { background: "#fff", color: "#52606d", border: "1px solid #f3e8d0" }
                }
              >
                <TabIcon size={13} />
                {label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-8 h-8 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading submission history…</p>
          </div>
        )}

        {!loading && fetchError && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-4">
              <X size={24} className="text-[#DC143C]" />
            </div>
            <p className="text-base font-semibold text-[#1f2937] mb-1">Couldn't load submission history</p>
            <p className="text-sm text-gray-500">{fetchError}</p>
          </div>
        )}

        {!loading && !fetchError && rows?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
              <FileText size={24} className="text-amber-400" />
            </div>
            <p className="text-base font-semibold text-[#1f2937]">No submissions yet.</p>
          </div>
        )}

        {/* ── By Factory view ── */}
        {!loading && !fetchError && rows?.length > 0 && activeView === "factory" && (
          <div className="space-y-3">
            {byFactory?.map((f) => {
              const isOpen = expandedFactory === f?.millId;
              return (
                <div key={f?.millId} className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid #f3e8d0" }}>
                  <button
                    onClick={() => setExpandedFactory(isOpen ? null : f?.millId)}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-amber-50/40 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-sm font-bold text-[#1f2937]">{f?.millName}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {f?.totalSubmissions} submission{f?.totalSubmissions !== 1 ? "s" : ""} · Live qty: {f?.liveQty} MT
                        {isAdminView && f?.lastSubmittedByName && <> · Last by {f?.lastSubmittedByName}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-500">{formatDate(f?.lastSubmittedAt)}</span>
                      {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-2" style={{ borderTop: "1px solid #f3e8d0" }}>
                      {f?.rows
                        ?.slice()
                        ?.sort((a, b) => (a?.submittedAt < b?.submittedAt ? 1 : -1))
                        ?.map((r) => (
                          <div key={r?.id} className="flex items-center justify-between gap-3 py-2 text-xs" style={{ borderBottom: "1px dashed #f3e8d0" }}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <StatusBadge status={r?.status} />
                              <span className="font-semibold text-[#1f2937]">{r?.grade}</span>
                              <span className="text-gray-500">{r?.qty} MT</span>
                              {isAdminView && r?.submittedByName && <span className="text-gray-400">· by {r?.submittedByName}</span>}
                            </div>
                            <span className="text-gray-400 flex-shrink-0">{formatDate(r?.submittedAt)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── By Officer view (admin/quota_manager only) ── */}
        {!loading && !fetchError && rows?.length > 0 && activeView === "officer" && isAdminView && (
          <div className="space-y-3">
            {byOfficer?.map((o) => {
              const isOpen = expandedOfficer === o?.submittedBy;
              return (
                <div key={o?.submittedBy} className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid #f3e8d0" }}>
                  <button
                    onClick={() => setExpandedOfficer(isOpen ? null : o?.submittedBy)}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-amber-50/40 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-sm font-bold text-[#1f2937]">{o?.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {o?.totalSubmissions} submission{o?.totalSubmissions !== 1 ? "s" : ""} · {o?.factoriesCount} factor{o?.factoriesCount !== 1 ? "ies" : "y"} · {o?.approvalRate}% approval rate
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-500">{o?.liveQty} MT live</span>
                      {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-2" style={{ borderTop: "1px solid #f3e8d0" }}>
                      {o?.rows
                        ?.slice()
                        ?.sort((a, b) => (a?.submittedAt < b?.submittedAt ? 1 : -1))
                        ?.map((r) => (
                          <div key={r?.id} className="flex items-center justify-between gap-3 py-2 text-xs" style={{ borderBottom: "1px dashed #f3e8d0" }}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <StatusBadge status={r?.status} />
                              <span className="font-semibold text-[#1f2937]">{r?.millName}</span>
                              <span className="text-gray-500">{r?.grade} · {r?.qty} MT</span>
                            </div>
                            <span className="text-gray-400 flex-shrink-0">{formatDate(r?.submittedAt)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── All Submissions (raw log) view ── */}
        {/* ── Expired view: still live, still has stock, lifting date passed, and not
             yet superseded by a newer live/pending submission for the same mill+grade.
             No forced action — stays here as information until someone acts on it. ── */}
        {!loading && !fetchError && activeView === "expired" && (
          expiredRows?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                <AlertTriangle size={24} className="text-green-400" />
              </div>
              <p className="text-base font-semibold text-[#1f2937]">Nothing's expired right now.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {expiredRows?.map((r) => (
                <div key={r?.id} className="bg-white rounded-xl px-4 py-3" style={{ border: "1px solid #fed7aa" }}>
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status="expired" />
                      <span className="text-sm font-bold text-[#1f2937]">{r?.millName}</span>
                      <span className="text-xs text-gray-500">{r?.grade} · {r?.qty} MT</span>
                      {isAdminView && r?.submittedByName && (
                        <span className="text-xs text-gray-400">· submitted by {r?.submittedByName}</span>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-[#9a3412] flex-shrink-0">
                      Lifting date was {formatDate(r?.liftingDate)}
                    </span>
                  </div>
                  {canResubmit && (
                    <button
                      onClick={() => navigate(`/factory-rate-intelligence-center?millId=${r?.millId}`)}
                      className="w-full mt-1 px-3 py-2 rounded-lg text-xs font-bold text-white transition-colors"
                      style={{ background: "#9a3412" }}
                    >
                      Update &amp; Resubmit
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {!loading && !fetchError && rows?.length > 0 && activeView === "log" && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search by mill name…"
                  className="w-full pl-9 pr-4 py-2 text-sm rounded-xl bg-white text-[#1f2937] placeholder-gray-400 focus:outline-none focus:ring-2 shadow-sm"
                  style={{ border: "1px solid #f3e8d0" }}
                />
              </div>
              {isAdminView && (
                <select
                  value={officerFilter}
                  onChange={(e) => setOfficerFilter(e?.target?.value)}
                  className="px-3 py-2 rounded-xl text-sm bg-white"
                  style={{ border: "1px solid #f3e8d0", color: "#1f2937" }}
                >
                  <option value="all">All Officers</option>
                  {officerOptions?.map((o) => (
                    <option key={o?.id} value={o?.id}>{o?.name}</option>
                  ))}
                </select>
              )}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-3 py-2 rounded-xl text-sm bg-white"
                style={{ border: "1px solid #f3e8d0", color: "#1f2937" }}
              >
                <option value="all">All Statuses</option>
                <option value="live">Live</option>
                <option value="pending_approval">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
            </div>

            {filteredLog?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Search size={24} className="text-gray-400" />
                </div>
                <p className="text-base font-semibold text-[#1f2937]">No submissions match these filters.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLog?.map((r) => (
                  <div key={r?.id} className="bg-white rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ border: "1px solid #f3e8d0" }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={r?.status} />
                      <span className="text-sm font-bold text-[#1f2937]">{r?.millName}</span>
                      <span className="text-xs text-gray-500">{r?.grade} · {r?.season}</span>
                      <span className="text-xs text-gray-500">{r?.qty} MT</span>
                      {isAdminView && r?.submittedByName && (
                        <span className="text-xs text-gray-400">· by {r?.submittedByName}</span>
                      )}
                      {r?.status === "rejected" && r?.rejectionReason && (
                        <span className="text-xs text-[#b91c1c]">· {r?.rejectionReason}</span>
                      )}
                      {r?.status === "withdrawn" && (
                        <span className="text-xs text-gray-500">
                          · withdrawn{r?.withdrawnByName ? ` by ${r?.withdrawnByName}` : ""}{r?.withdrawnAt ? ` on ${formatDate(r?.withdrawnAt)}` : ""}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
                      Submitted {formatDate(r?.submittedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && !fetchError && activeView === "emails" && (
          <EmailsTab isAdminView={isAdminView} />
        )}
      </div>
    </div>
  );
};

export default SubmissionHistory;
